import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Html, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

// Theatre mode: `theatreRef.current` blends 0 (gallery) → 1 (cinema). Like night mode, everything
// stays mounted and only scales/fades, so no shader ever compiles mid-visit (see NIGHT_AND_CLIMATE.md).

// Layout (the hall is 24 x 24 m; the north wall is at z = -12).
export const THEATRE = {
  screen: { position: [0, 3.05, -10.6], width: 7.2, height: 4.05 }, // 16:9
  recliner: [0, 0, -4.4],
  seatEye: new THREE.Vector3(0, 1.55, -4.05),  // where the camera "sits"
  lookAt: new THREE.Vector3(0, 2.95, -10.6),   // centre of the screen
};

const ease = (t) => t * t * (3 - 2 * t);
const hide = (obj, k) => obj && obj.scale.setScalar(k > 0.001 ? k : 0.0001);

export const TheatreDriver = ({ active, theatreRef }) => {
  const { scene } = useThree();
  useFrame((_, delta) => {
    const target = active ? 1 : 0;
    const c = theatreRef.current;
    if (c !== target) {
      const step = Math.min(delta * 0.6, Math.abs(target - c));
      theatreRef.current = c + Math.sign(target - c) * step;
    }
    // Runs after the day/night and weather drivers: dim reflections for the cinema.
    scene.environmentIntensity *= 1 - 0.85 * theatreRef.current;
  });
  return null;
};

// --- Low-poly recliner (built from a few rounded boxes, ~2k triangles) ---------------------
const LEATHER = { color: '#6b1420', roughness: 0.45, metalness: 0.05 };
const Recliner = ({ theatreRef }) => {
  const ref = useRef();
  useFrame(() => {
    const t = ease(theatreRef.current);
    if (!ref.current) return;
    hide(ref.current, t);
    ref.current.position.y = (1 - t) * -0.6; // rises out of the floor
  });
  return (
    // Faces the screen (-z). Back is towards +z.
    <group position={THEATRE.recliner}>
      <group ref={ref} scale={0.0001}>
        {/* base */}
        <mesh position={[0, 0.12, 0]}>
          <boxGeometry args={[1.05, 0.24, 0.95]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
        </mesh>
        {/* seat cushion */}
        <RoundedBox args={[0.9, 0.26, 0.85]} radius={0.08} smoothness={3} position={[0, 0.42, -0.02]}>
          <meshStandardMaterial {...LEATHER} />
        </RoundedBox>
        {/* reclined backrest */}
        <RoundedBox args={[0.9, 1.0, 0.24]} radius={0.09} smoothness={3} position={[0, 0.95, 0.5]} rotation={[-0.38, 0, 0]}>
          <meshStandardMaterial {...LEATHER} />
        </RoundedBox>
        {/* headrest pillow */}
        <RoundedBox args={[0.6, 0.24, 0.16]} radius={0.07} smoothness={3} position={[0, 1.4, 0.62]} rotation={[-0.38, 0, 0]}>
          <meshStandardMaterial {...LEATHER} color="#7d1b28" />
        </RoundedBox>
        {/* armrests */}
        {[-1, 1].map((s) => (
          <RoundedBox key={s} args={[0.2, 0.32, 0.95]} radius={0.07} smoothness={3} position={[s * 0.55, 0.62, 0.02]}>
            <meshStandardMaterial {...LEATHER} />
          </RoundedBox>
        ))}
        {/* extended footrest */}
        <RoundedBox args={[0.86, 0.14, 0.55]} radius={0.06} smoothness={3} position={[0, 0.42, -0.72]} rotation={[0.18, 0, 0]}>
          <meshStandardMaterial {...LEATHER} />
        </RoundedBox>
      </group>
    </group>
  );
};

// --- Velvet curtains either side of the screen -------------------------------------------
const makeCurtainGeometry = (w, h) => {
  const g = new THREE.PlaneGeometry(w, h, 48, 1);
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    pos.setZ(i, Math.sin((x / w) * Math.PI * 14) * 0.09); // folds
  }
  g.computeVertexNormals();
  g.translate(0, -h / 2, 0); // hang from the top edge
  return g;
};
const Curtains = ({ theatreRef }) => {
  const refs = [useRef(), useRef()];
  const geometry = useMemo(() => makeCurtainGeometry(1.7, 5.6), []);
  useFrame(() => {
    const t = ease(theatreRef.current);
    refs.forEach((r) => {
      if (!r.current) return;
      r.current.scale.set(t > 0.001 ? 1 : 0.0001, Math.max(t, 0.0001), 1); // drop from the top
    });
  });
  const { position, width } = THEATRE.screen;
  return [-1, 1].map((s, i) => (
    <group key={s} ref={refs[i]} position={[s * (width / 2 + 0.85), 5.9, position[2] + 0.25]} scale={0.0001}>
      <mesh geometry={geometry}>
        <meshStandardMaterial color="#7a0f1c" roughness={0.85} side={THREE.DoubleSide} />
      </mesh>
    </group>
  ));
};

// --- Screen: bezel, dark panel, glow on the wall, and the video (desktop) -----------------
const makeGlowTexture = () => {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(64, 64, 10, 64, 64, 64);
  g.addColorStop(0, 'rgba(120,150,255,0.9)');
  g.addColorStop(1, 'rgba(120,150,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
};

const Screen = ({ theatreRef, children }) => {
  const groupRef = useRef();
  const glowRef = useRef();
  const glowTex = useMemo(makeGlowTexture, []);
  const { position, width, height } = THEATRE.screen;
  useFrame((state) => {
    const t = ease(theatreRef.current);
    if (groupRef.current) {
      hide(groupRef.current, t);
      groupRef.current.position.y = position[1] + (1 - t) * 3; // rolls down from the ceiling
    }
    if (glowRef.current) glowRef.current.opacity = t * (0.35 + Math.sin(state.clock.elapsedTime * 1.7) * 0.05);
  });
  return (
    <group ref={groupRef} position={position} scale={0.0001}>
      <mesh position={[0, 0, -0.12]} renderOrder={2}>
        <planeGeometry args={[width * 1.9, height * 2]} />
        <meshBasicMaterial ref={glowRef} map={glowTex} transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0, -0.06]}>
        <boxGeometry args={[width + 0.3, height + 0.3, 0.1]} />
        <meshStandardMaterial color="#0c0c0e" roughness={0.4} metalness={0.3} />
      </mesh>
      <mesh>
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial color="#06080c" toneMapped={false} />
      </mesh>
      {children}
    </group>
  );
};

// YouTube player placed on the 3D screen. distanceFactor 2 → 200 CSS px per metre (crisp enough).
const PX_PER_M = 200;
export const ScreenVideo = ({ children }) => {
  const { width, height } = THEATRE.screen;
  return (
    <Html transform distanceFactor={2} position={[0, 0, 0.01]} zIndexRange={[20, 0]} pointerEvents="auto">
      <div style={{ width: width * PX_PER_M, height: height * PX_PER_M, background: '#000' }}>{children}</div>
    </Html>
  );
};

// --- Aisle lights leading to the seat -----------------------------------------------------
const AisleLights = ({ theatreRef }) => {
  const material = useMemo(() => new THREE.MeshBasicMaterial({ color: '#000000', toneMapped: false }), []);
  const spots = useMemo(() => {
    const out = [];
    for (let i = 0; i < 6; i++) for (const s of [-1, 1]) out.push([s * 1.4, 0.02, -2 - i * 1.4]);
    return out;
  }, []);
  const groupRef = useRef();
  useFrame(() => {
    material.color.setRGB(1, 0.62, 0.25).multiplyScalar(theatreRef.current * 1.6);
    hide(groupRef.current, theatreRef.current > 0.001 ? 1 : 0);
  });
  return (
    <group ref={groupRef} scale={0.0001}>
      {spots.map((p, i) => (
        <mesh key={i} position={p} rotation={[-Math.PI / 2, 0, 0]} material={material} frustumCulled={false}>
          <circleGeometry args={[0.07, 12]} />
        </mesh>
      ))}
    </group>
  );
};

export const TheatreSet = ({ theatreRef, video }) => {
  const ref = useRef();
  // Always draw (even while shrunk to nothing) so the shader warm-up compiles everything up front.
  useEffect(() => {
    ref.current?.traverse((o) => { o.frustumCulled = false; });
  }, []);
  return (
    <group ref={ref}>
      <Recliner theatreRef={theatreRef} />
      <Screen theatreRef={theatreRef}>{video}</Screen>
      <Curtains theatreRef={theatreRef} />
      <AisleLights theatreRef={theatreRef} />
    </group>
  );
};
